import React, {
  ElementRef,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { Platform, View } from "react-native";
import { Popable, PopableManager } from "react-native-popable";

import { Box } from "../../../../primitive";

type TooltipProps = {
  direction?: "top" | "bottom" | "left" | "right";
  size?: "small" | "large";
  trigger: ReactNode;
  content: ReactNode;
  backgroundColor?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
  accessibilityRole?: "button" | "group";
};

type OpenReason = "focus" | "hover" | "press";

type OpenReasons = Record<OpenReason, boolean>;

const closedReasons: OpenReasons = {
  focus: false,
  hover: false,
  press: false,
};

const isEscapeKey = (event: unknown) =>
  typeof event === "object" &&
  event !== null &&
  Reflect.get(event, "key") === "Escape";

export const Tooltip = ({
  direction = "top",
  size = "small",
  trigger,
  content,
  backgroundColor = undefined,
  accessibilityLabel,
  accessibilityHint,
  accessibilityRole = "button",
}: TooltipProps) => {
  const tooltipId = useId();
  const containerRef = useRef<ElementRef<typeof View>>(null);
  const popableRef = useRef<PopableManager>(null);
  const [openReasons, setOpenReasons] = useState<OpenReasons>(closedReasons);
  const isVisible = openReasons.focus || openReasons.hover || openReasons.press;

  const setReason = useCallback((reason: OpenReason, isActive: boolean) => {
    setOpenReasons((reasons) =>
      reasons[reason] === isActive
        ? reasons
        : { ...reasons, [reason]: isActive }
    );
  }, []);

  const show = useCallback(
    (reason: OpenReason) => setReason(reason, true),
    [setReason]
  );

  const hideReason = useCallback(
    (reason: OpenReason) => setReason(reason, false),
    [setReason]
  );

  const handleBlur = useCallback(() => {
    setOpenReasons((reasons) =>
      reasons.focus || reasons.press
        ? { ...reasons, focus: false, press: false }
        : reasons
    );
  }, []);

  const hide = useCallback(() => setOpenReasons(closedReasons), []);

  const handlePress = useCallback(() => {
    setOpenReasons((reasons) =>
      reasons.press ? closedReasons : { ...reasons, press: true }
    );
  }, []);

  useEffect(() => {
    if (Platform.OS === "web") {
      return;
    }

    if (isVisible) {
      popableRef.current?.show();
    } else {
      popableRef.current?.hide();
    }
  }, [isVisible]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") {
      return;
    }

    const container = containerRef.current;

    if (typeof Node === "undefined" || !(container instanceof Node)) {
      return;
    }

    const handleKeyDown = (event: Event) => {
      if (isEscapeKey(event)) {
        hide();
      } else if (
        accessibilityLabel !== undefined &&
        accessibilityRole === "group" &&
        event instanceof KeyboardEvent &&
        (event.key === "Enter" || event.key === " ")
      ) {
        event.preventDefault();
        handlePress();
      }
    };
    const handleOutsidePress = (event: PointerEvent) => {
      if (event.target instanceof Node && !container.contains(event.target)) {
        hide();
      }
    };

    container.addEventListener("keydown", handleKeyDown);

    if (isVisible) {
      document.addEventListener("pointerdown", handleOutsidePress);
    }

    return () => {
      container.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handleOutsidePress);
    };
  }, [accessibilityLabel, accessibilityRole, handlePress, hide, isVisible]);

  const wrapperOwnsAccessibility = accessibilityLabel !== undefined;
  const triggerProps = {
    ...(wrapperOwnsAccessibility
      ? {
          "aria-describedby": Platform.OS === "web" ? tooltipId : undefined,
          accessibilityHint,
          accessibilityLabel,
          role: accessibilityRole,
          tabIndex: 0 as const,
        }
      : {
          accessible: false,
          focusable: false,
          tabIndex: -1 as const,
        }),
    onBlur: handleBlur,
    onFocus: () => show("focus"),
    onHoverIn: () => show("hover"),
    onHoverOut: () => hideReason("hover"),
    onKeyDown: (event: unknown) => {
      if (isEscapeKey(event)) {
        hide();
      }
    },
    onPress: handlePress,
  };

  return (
    <View ref={containerRef}>
      <Popable
        ref={popableRef}
        action="hover"
        triggerProps={triggerProps}
        visible={Platform.OS === "web" ? isVisible : undefined}
        onAction={(visible) => {
          if (Platform.OS !== "web") {
            if (visible) {
              show("press");
            } else {
              hide();
            }
          }
        }}
        style={{
          width: size === "large" ? 200 : "auto",
        }}
        position={direction}
        backgroundColor={backgroundColor}
        content={
          <Box
            nativeID={tooltipId}
            role="tooltip"
            testID="tooltip-content"
            aria-hidden={!isVisible}
            paddingHorizontal={3}
            paddingVertical={2}
            backgroundColor="surface-inverse"
            flex={1}
          >
            {content}
          </Box>
        }
      >
        {trigger}
      </Popable>
    </View>
  );
};
